// ╔══════════════════════════════════════════════════════════════╗
// ║  Employees page — list, add, edit, delete, export             ║
// ║  Enforces 100-employee hard limit visually + via API.         ║
// ╚══════════════════════════════════════════════════════════════╝

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, downloadFile, getErrorMessage } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { useToast } from '../components/Toast.jsx';
import { fmtAR, fmtMoney, STATUS_LABEL_AR } from '../lib/format.js';
import {
  Avatar,
  Badge,
  Btn,
  Card,
  Chip,
  Field,
  Input,
  Modal,
  Pills,
  Progress,
  Select,
} from '../components/ui.jsx';
import Icon from '../components/Icon.jsx';

const STATUS_TONES = {
  active: 'success',
  leave: 'info',
  probation: 'warn',
  terminated: 'neutral',
};

export default function EmployeesPage() {
  const { user } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    search: '',
    deptId: 'all',
    shiftId: 'all',
    status: 'all',
  });
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null); // employee object or { new: true }
  const [deleting, setDeleting] = useState(null);

  // ─ Queries ─
  const { data: deptsData } = useQuery({
    queryKey: ['departments'],
    queryFn: () => api.get('/departments').then((r) => r.data),
  });
  const { data: shiftsData } = useQuery({
    queryKey: ['shifts'],
    queryFn: () => api.get('/shifts').then((r) => r.data),
  });

  const queryParams = {
    page,
    pageSize: 20,
    ...(filters.search && { search: filters.search }),
    ...(filters.deptId !== 'all' && { deptId: filters.deptId }),
    ...(filters.shiftId !== 'all' && { shiftId: filters.shiftId }),
    ...(filters.status !== 'all' && { status: filters.status }),
  };

  const { data, isLoading } = useQuery({
    queryKey: ['employees', queryParams],
    queryFn: () =>
      api.get('/employees', { params: queryParams }).then((r) => r.data),
    keepPreviousData: true,
  });

  const employees = data?.employees ?? [];
  const pagination = data?.pagination;
  const limits = data?.limits;
  const canEdit = user?.role === 'admin' || user?.role === 'acc';
  const canDelete = user?.role === 'admin';

  // ─ Mutations ─
  const saveMut = useMutation({
    mutationFn: (payload) => {
      if (editing?.id) return api.patch(`/employees/${editing.id}`, payload);
      return api.post('/employees', payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees'] });
      qc.invalidateQueries({ queryKey: ['reports', 'dashboard'] });
      toast.push({
        tone: 'success',
        title: editing?.id ? 'تم تحديث الموظف' : 'تم إضافة الموظف',
      });
      setEditing(null);
    },
    onError: (err) => {
      toast.push({ tone: 'danger', title: 'فشل الحفظ', body: getErrorMessage(err) });
    },
  });

  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/employees/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees'] });
      qc.invalidateQueries({ queryKey: ['reports', 'dashboard'] });
      toast.push({ tone: 'success', title: 'تم حذف الموظف' });
      setDeleting(null);
    },
    onError: (err) => {
      toast.push({ tone: 'danger', title: 'فشل الحذف', body: getErrorMessage(err) });
    },
  });

  function handleExport() {
    const qs = new URLSearchParams(
      Object.entries(queryParams).filter(([k]) => k !== 'page' && k !== 'pageSize'),
    ).toString();
    downloadFile(`/employees/export?${qs}`, `employees_${new Date().toISOString().slice(0, 10)}.csv`)
      .catch((err) => toast.push({ tone: 'danger', title: 'فشل التحميل', body: getErrorMessage(err) }));
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 fade-up">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-brand-500 font-bold">
            قاعدة بيانات الموظفين
          </div>
          <h1 className="text-2xl md:text-3xl font-black mt-1">
            الموظفون{' '}
            {pagination && (
              <span className="text-ink-400 font-bold tabular">· {fmtAR(pagination.total)}</span>
            )}
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Btn variant="outline" icon="download" onClick={handleExport}>
            تصدير
          </Btn>
          {canEdit && (
            <Btn
              variant="primary"
              icon="plus"
              disabled={limits?.atCapacity}
              onClick={() => setEditing({ new: true })}
              title={limits?.atCapacity ? 'وصلت للحد الأقصى للموظفين' : ''}
            >
              إضافة موظف
            </Btn>
          )}
        </div>
      </div>

      {/* Limit indicator hidden from end users — admin can see in /settings */}

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Icon name="search" className="absolute top-3 right-3 text-ink-400" size={16} />
            <input
              value={filters.search}
              onChange={(e) => {
                setFilters((f) => ({ ...f, search: e.target.value }));
                setPage(1);
              }}
              placeholder="ابحث بالاسم أو رقم الموظف…"
              className="w-full h-10 px-4 pe-10 rounded-xl bg-ink-50 dark:bg-ink-800 ring-1 ring-ink-200 dark:ring-ink-700 focus:ring-2 focus:ring-brand-400 outline-none text-sm"
            />
          </div>
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
            <Chip
              active={filters.deptId === 'all'}
              onClick={() => {
                setFilters((f) => ({ ...f, deptId: 'all' }));
                setPage(1);
              }}
            >
              كل الأقسام
            </Chip>
            {deptsData?.departments?.slice(0, 7).map((d) => (
              <Chip
                key={d.id}
                active={filters.deptId === d.id}
                onClick={() => {
                  setFilters((f) => ({ ...f, deptId: d.id }));
                  setPage(1);
                }}
              >
                {d.nameAr}
              </Chip>
            ))}
          </div>
          <Pills
            value={filters.status}
            onChange={(v) => {
              setFilters((f) => ({ ...f, status: v }));
              setPage(1);
            }}
            items={[
              { value: 'all', label: 'الكل' },
              { value: 'active', label: 'نشط' },
              { value: 'leave', label: 'إجازة' },
              { value: 'probation', label: 'تجربة' },
            ]}
          />
        </div>
      </Card>

      {/* List — table on desktop, cards on mobile */}
      <Card className="overflow-hidden">
        {/* Desktop table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 dark:bg-ink-800/40 text-xs text-ink-500">
              <tr>
                {['الكود', 'الموظف', 'القسم', 'المسمى', 'الراتب', 'الحالة', ''].map((h, i) => (
                  <th key={i} className="text-start font-semibold px-4 py-3 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-soft">
              {isLoading && (
                <tr>
                  <td colSpan="7" className="p-12 text-center text-ink-400">
                    جاري التحميل…
                  </td>
                </tr>
              )}
              {!isLoading && employees.length === 0 && (
                <tr>
                  <td colSpan="7" className="p-12 text-center text-ink-400">
                    لا توجد نتائج
                  </td>
                </tr>
              )}
              {employees.map((e) => (
                <tr
                  key={e.id}
                  onClick={() => navigate(`/employees/${e.id}`)}
                  className="hover:bg-brand-50/50 dark:hover:bg-brand-950/30 transition cursor-pointer"
                >
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center justify-center min-w-[44px] h-7 px-2 rounded-lg bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-200 font-mono text-[13px] font-extrabold tabular tracking-wider">
                      {e.id}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar name={e.name} size={36} />
                      <div>
                        <div className="font-semibold leading-tight">{e.name}</div>
                        {e.phone && (
                          <div className="text-[11px] text-ink-400 font-mono">{e.phone}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-ink-500 dark:text-ink-300">{e.deptId}</td>
                  <td className="px-4 py-3 text-ink-500 dark:text-ink-300">{e.position}</td>
                  <td className="px-4 py-3 tabular font-semibold">{fmtMoney(e.baseSalary)}</td>
                  <td className="px-4 py-3">
                    <Badge tone={STATUS_TONES[e.status]} dot>
                      {STATUS_LABEL_AR[e.status]}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-end" onClick={(ev) => ev.stopPropagation()}>
                    {(canEdit || canDelete) && (
                      <div className="inline-flex gap-1">
                        {canEdit && (
                          <button
                            onClick={() => setEditing(e)}
                            title="تعديل"
                            className="w-8 h-8 grid place-items-center rounded-lg hover:bg-ink-100 dark:hover:bg-ink-800"
                          >
                            <Icon name="edit" size={14} />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => setDeleting(e)}
                            title="حذف"
                            className="w-8 h-8 grid place-items-center rounded-lg hover:bg-rose-100 dark:hover:bg-rose-950/30 text-rose-600"
                          >
                            <Icon name="trash" size={14} />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="lg:hidden divide-soft">
          {isLoading && (
            <div className="p-12 text-center text-ink-400 text-sm">جاري التحميل…</div>
          )}
          {!isLoading && employees.length === 0 && (
            <div className="p-12 text-center text-ink-400 text-sm">لا توجد نتائج</div>
          )}
          {employees.map((e) => (
            <div
              key={e.id}
              onClick={() => navigate(`/employees/${e.id}`)}
              className="p-4 cursor-pointer hover:bg-brand-50/40 dark:hover:bg-brand-950/20 transition"
            >
              <div className="flex items-start gap-3">
                <Avatar name={e.name} size={40} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center min-w-[40px] h-6 px-2 rounded-md bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-200 font-mono text-[12px] font-extrabold tabular">
                      {e.id}
                    </span>
                    <div className="font-semibold truncate">{e.name}</div>
                  </div>
                  <div className="text-xs text-ink-500 mt-1">{e.position}</div>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge tone={STATUS_TONES[e.status]} dot>
                      {STATUS_LABEL_AR[e.status]}
                    </Badge>
                    <Badge tone="neutral">{e.deptId}</Badge>
                  </div>
                </div>
                <div className="text-end">
                  <div className="font-bold tabular text-sm">{fmtAR(e.baseSalary)}</div>
                  <div className="text-[10px] text-ink-400">EGP</div>
                </div>
              </div>
              {(canEdit || canDelete) && (
                <div
                  className="flex gap-2 mt-3"
                  onClick={(ev) => ev.stopPropagation()}
                >
                  {canEdit && (
                    <Btn variant="outline" size="sm" icon="edit" onClick={() => setEditing(e)}>
                      تعديل
                    </Btn>
                  )}
                  {canDelete && (
                    <Btn variant="ghost" size="sm" icon="trash" onClick={() => setDeleting(e)}>
                      حذف
                    </Btn>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between p-4 text-xs text-ink-400 border-t border-ink-100 dark:border-ink-800">
            <span>
              {fmtAR(pagination.page)} من {fmtAR(pagination.totalPages)}
            </span>
            <div className="flex items-center gap-1">
              <Btn
                variant="ghost"
                size="sm"
                icon="chevR"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={pagination.page <= 1}
              >
                السابق
              </Btn>
              <Btn
                variant="ghost"
                size="sm"
                iconEnd="chevL"
                onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                disabled={pagination.page >= pagination.totalPages}
              >
                التالي
              </Btn>
            </div>
          </div>
        )}
      </Card>

      {/* Add/Edit modal */}
      {editing && (
        <EmployeeForm
          employee={editing.new ? null : editing}
          departments={deptsData?.departments ?? []}
          shifts={shiftsData?.shifts ?? []}
          onClose={() => setEditing(null)}
          onSave={(p) => saveMut.mutate(p)}
          loading={saveMut.isPending}
        />
      )}

      {/* Delete confirm */}
      {deleting && (
        <Modal
          open
          onClose={() => setDeleting(null)}
          title="حذف موظف"
          size="sm"
          footer={
            <>
              <Btn variant="ghost" onClick={() => setDeleting(null)}>
                إلغاء
              </Btn>
              <Btn
                variant="danger"
                icon="trash"
                loading={delMut.isPending}
                onClick={() => delMut.mutate(deleting.id)}
              >
                تأكيد الحذف
              </Btn>
            </>
          }
        >
          <p className="text-sm">
            هل أنت متأكد من حذف <span className="font-bold">{deleting.name}</span>؟
          </p>
          <p className="text-xs text-rose-600 mt-2">
            هذا الإجراء لا يمكن التراجع عنه.
          </p>
        </Modal>
      )}
    </div>
  );
}

// ─── Employee Form (add/edit) ───
function EmployeeForm({ employee, departments, shifts, onClose, onSave, loading }) {
  const isEdit = !!employee;
  const [form, setForm] = useState({
    name: employee?.name ?? '',
    gender: employee?.gender ?? 'M',
    deptId: employee?.deptId ?? departments[0]?.id ?? '',
    shiftId: employee?.shiftId ?? 'A',
    position: employee?.position ?? '',
    baseSalary: employee?.baseSalary ?? '',
    allowance: employee?.allowance ?? '',
    insuranceAmount: employee?.insuranceAmount ?? '',
    phone: employee?.phone ?? '',
    nid: employee?.nid ?? '',
    hiredOn: employee?.hiredOn ?? new Date().toISOString().slice(0, 10),
    address: employee?.address ?? '',
    status: employee?.status ?? 'active',
  });

  function handleSubmit(e) {
    e.preventDefault();
    const payload = { ...form };
    // strip empty optional fields
    if (!payload.phone) delete payload.phone;
    if (!payload.nid) delete payload.nid;
    if (!payload.address) delete payload.address;
    onSave(payload);
  }

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? `تعديل: ${employee.name}` : 'إضافة موظف جديد'}
      size="lg"
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>إلغاء</Btn>
          <Btn type="submit" form="emp-form" variant="primary" icon="check" loading={loading}>
            {isEdit ? 'حفظ التغييرات' : 'إضافة'}
          </Btn>
        </>
      }
    >
      <form id="emp-form" onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="الاسم الكامل">
          <Input value={form.name} onChange={upd('name')} required minLength={2} />
        </Field>
        <Field label="النوع">
          <Select value={form.gender} onChange={upd('gender')}>
            <option value="M">ذكر</option>
            <option value="F">أنثى</option>
          </Select>
        </Field>
        <Field label="القسم">
          <Select value={form.deptId} onChange={upd('deptId')} required>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>{d.nameAr}</option>
            ))}
          </Select>
        </Field>
        <Field label="الوردية">
          <Select value={form.shiftId} onChange={upd('shiftId')} required>
            {shifts.map((s) => (
              <option key={s.id} value={s.id}>{s.nameAr}</option>
            ))}
          </Select>
        </Field>
        <Field label="المسمى الوظيفي">
          <Input value={form.position} onChange={upd('position')} required minLength={2} />
        </Field>
        <Field label="الحالة">
          <Select value={form.status} onChange={upd('status')}>
            <option value="active">نشط</option>
            <option value="probation">تحت التجربة</option>
            <option value="leave">في إجازة</option>
            <option value="terminated">منتهية الخدمة</option>
          </Select>
        </Field>
        <Field label="الراتب الأساسي (جنيه)">
          <Input type="number" value={form.baseSalary} onChange={upd('baseSalary')} required min={0} />
        </Field>
        <Field label="البدلات (جنيه)">
          <Input type="number" value={form.allowance} onChange={upd('allowance')} min={0} />
        </Field>
        <Field label="التأمينات (جنيه)">
          <Input type="number" value={form.insuranceAmount} onChange={upd('insuranceAmount')} min={0} />
        </Field>
        <Field label="الهاتف">
          <Input value={form.phone} onChange={upd('phone')} dir="ltr" />
        </Field>
        <Field label="الرقم القومي" hint="14 رقم">
          <Input value={form.nid} onChange={upd('nid')} dir="ltr" maxLength={14} pattern="\d{14}" />
        </Field>
        <Field label="تاريخ التعيين">
          <Input type="date" value={form.hiredOn} onChange={upd('hiredOn')} required />
        </Field>
        <div className="sm:col-span-2">
          <Field label="العنوان">
            <Input value={form.address} onChange={upd('address')} />
          </Field>
        </div>
      </form>
    </Modal>
  );
}
